import * as React from "react";
import { useEffect, useCallback } from "react";
import {
  Table,
  SpaceBetween,
  Header,
  Container,
  TableProps,
  Spinner,
} from "@amzn/awsui-components-console";
import {
  Ard,
  UseCase,
  UslLine,
} from "@amzn/fuse-accounting-central-datastore-client";
import { UseCaseTableCell } from "./use-case-table-cell";
import { useFlareContext } from "src/context/flare/flareContext";
import { UslLineDiscrepancies } from "src/context/flare/flare-comparison-service";
import { SEVEN_SEGMENTS_USL_LINE_FIELDS } from "src/context/flare/constants";
import {
  SPLIT_PANEL_STICKY_HEADER_OFFSET,
  ANCHOR_SCROLL_MARGIN_TOP,
  getUseCaseSectionId,
} from "./split-panel.helper";
import { useJemGen } from "src/context/jem-gen/jemGenContext";

interface UseCaseTablesProps {
  displayedArd: Ard | null;
}

export function UseCaseTables({ displayedArd }: UseCaseTablesProps) {
  const { ardFlareComparison, compareArdWithFlare, flareConfig } =
    useFlareContext();
  const { coaDescriptions, isLoadingCoaDescriptions } = useJemGen();

  useEffect(() => {
    compareArdWithFlare(displayedArd);
  }, [displayedArd, flareConfig]);

  // Memoized helper function to get discrepancies for a specific use case and USL line
  const getUslLineDiscrepancies = useCallback(
    (
      useCaseName: string,
      uslLineIndex: number,
    ): UslLineDiscrepancies | undefined => {
      if (!ardFlareComparison) return undefined;

      const useCaseDiscrepancies = ardFlareComparison.useCaseDiscrepancies.find(
        (uc) => uc.useCaseName === useCaseName,
      );

      if (!useCaseDiscrepancies) return undefined;

      return useCaseDiscrepancies.uslLineDiscrepancies[uslLineIndex];
    },
    [ardFlareComparison],
  );

  const createColumnDefinitions = useCallback(
    (
      useCaseName: string,
    ): TableProps.ColumnDefinition<{ uslLine: UslLine; index: number }>[] => {
      return [
        {
          id: "accountName",
          header: "Account Name",
          minWidth: 150,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const accountNumber = item.uslLine.accountNumber;
            if (isLoadingCoaDescriptions) {
              return <Spinner />;
            }
            if (!accountNumber) {
              return "-";
            }
            return coaDescriptions.get(`accountNumber:${accountNumber}`) || "-";
          },
        },
        {
          id: "companyCode",
          header: "Company Code",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.COMPANY_CODE}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.companyCode}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "location",
          header: "Location",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.LOCATION}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.location}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "costCenter",
          header: "Cost Center",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.COST_CENTER}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.costCenter}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "accountNumber",
          header: "Account Number",
          minWidth: 140,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.ACCOUNT_NUMBER}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.accountNumber}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "productLine",
          header: "Product Line",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.PRODUCT_LINE}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.productLine}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "channel",
          header: "Channel",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.CHANNEL}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.channel}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "project",
          header: "Project",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            const discrepancies = getUslLineDiscrepancies(
              useCaseName,
              item.index,
            );
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey={SEVEN_SEGMENTS_USL_LINE_FIELDS.PROJECT}
                useCaseName={useCaseName}
                uslLineIndex={item.index}
                discrepancy={discrepancies?.project}
                uslLineType={discrepancies?.uslLineType}
              />
            );
          },
        },
        {
          id: "debitOrCredit",
          header: "Debit/Credit",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey="debitOrCredit"
                useCaseName={useCaseName}
                uslLineIndex={item.index}
              />
            );
          },
        },
        {
          id: "glLineDescriptionSuffix",
          header: "GL Line Description Suffix",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey="glLineDescriptionSuffix"
                useCaseName={useCaseName}
                uslLineIndex={item.index}
              />
            );
          },
        },
        {
          id: "subscriptionProductLine",
          header: "Subscription Product Line",
          minWidth: 120,
          cell: (item: { uslLine: UslLine; index: number }) => {
            return (
              <UseCaseTableCell
                uslLine={item.uslLine}
                uslLineKey="subscriptionProductLine"
                useCaseName={useCaseName}
                uslLineIndex={item.index}
              />
            );
          },
        },
      ];
    },
    [getUslLineDiscrepancies, coaDescriptions, isLoadingCoaDescriptions],
  );

  // Get use cases from selected ARD
  const useCases = displayedArd?.usecases || [];

  // Helper function to get display name for use case
  const getUseCaseDisplayName = (useCase: UseCase): string => {
    if (useCase.name === "OTHER" && useCase.altName) {
      return `${useCase.name} (${useCase.altName})`;
    }
    return useCase.name || "Unknown";
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      {useCases.length === 0 ? (
        <Container>
          <SpaceBetween size="m">
            <div>No use cases found</div>
          </SpaceBetween>
        </Container>
      ) : (
        useCases.map((useCase: UseCase, index: number) => {
          const uslLines = useCase.uslLines || [];
          const useCaseName = getUseCaseDisplayName(useCase);

          // Transform USL lines to include index for discrepancy lookup
          const itemsWithIndex = uslLines.map((uslLine, idx) => ({
            uslLine,
            index: idx,
          }));

          return (
            <div
              key={`${useCaseName}-${index}`}
              id={getUseCaseSectionId(index)}
              style={{ scrollMarginTop: `${ANCHOR_SCROLL_MARGIN_TOP}px` }}
            >
              <Table
                stickyHeader
                stickyHeaderVerticalOffset={SPLIT_PANEL_STICKY_HEADER_OFFSET}
                columnDefinitions={createColumnDefinitions(
                  useCase.name || "Unknown",
                )}
                items={itemsWithIndex}
                sortingDisabled={false}
                empty={
                  <SpaceBetween size="m">
                    <div>No USL lines found for this use case</div>
                  </SpaceBetween>
                }
                header={
                  <Header variant="h3" description={useCase.altName}>
                    {useCaseName}
                  </Header>
                }
              />
            </div>
          );
        })
      )}
    </SpaceBetween>
  );
}
